import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { apiClient } from "../../api/client";
import { getBusinessPosts, createPost, deletePost } from "../../api/post.api";
import { ImageUpload, type UploadedImage } from "../../components/ui/ImageUpload";
import type { BusinessProfile, Post, PostImage } from "@abejon/shared";

type PostWithImages = Post & { images: PostImage[] };

export function PostsPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [postsList, setPostsList] = useState<PostWithImages[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [caption, setCaption] = useState("");
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!user || user.accountType !== "business") return;
    let cancelled = false;

    async function load() {
      try {
        const data = await apiClient<{ businesses: BusinessProfile[] }>(
          "/businesses?mine=true"
        ).catch(() => null);

        if (cancelled) return;

        if (data?.businesses?.[0]) {
          setBusiness(data.businesses[0]);
          const postsData = await getBusinessPosts(data.businesses[0].slug);
          if (!cancelled) setPostsList(postsData.posts);
        }
      } catch {
        // No business profile
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== "business") {
    return <Navigate to="/" replace />;
  }

  if (!loading && !business) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-xl font-bold text-gray-900">No Business Profile</h1>
        <p className="mt-2 text-sm text-gray-500">
          Create a business profile first in Settings.
        </p>
      </div>
    );
  }

  function resetForm() {
    setCaption("");
    setUploadedImages([]);
    setShowForm(false);
    setMessage("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (uploadedImages.length === 0) {
      setMessage("Add at least one image");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const { post, images } = await createPost({
        caption: caption || undefined,
        images: uploadedImages.map((img, i) => ({
          r2Key: img.key,
          imageUrl: img.publicUrl,
          sortOrder: i,
        })),
      });
      setPostsList((prev) => [{ ...post, images }, ...prev]);
      resetForm();
      setMessage("Post created!");
    } catch {
      setMessage("Failed to create post");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(post: PostWithImages) {
    if (!confirm("Delete this post? This cannot be undone.")) return;

    try {
      await deletePost(post.id);
      setPostsList((prev) => prev.filter((p) => p.id !== post.id));
      setMessage("Post deleted");
    } catch {
      setMessage("Failed to delete post");
    }
  }

  return (
    <div className="mx-auto max-w-lg py-8 px-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Posts</h1>
        {!showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            New Post
          </button>
        )}
      </div>

      {message && (
        <p className={`mb-4 text-sm ${message.includes("Failed") || message.includes("Add at least") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </p>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 space-y-4 rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-semibold">New Post</h2>

          <ImageUpload
            folder="posts"
            maxFiles={10}
            onUploadComplete={setUploadedImages}
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Caption (optional)
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Write a caption..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving || uploadedImages.length === 0}
              className="flex-1 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? "Posting..." : "Post"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Post list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
        </div>
      ) : postsList.length === 0 && !showForm ? (
        <div className="py-12 text-center">
          <p className="text-gray-500">No posts yet. Share your work!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {postsList.map((post) => (
            <div key={post.id} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start gap-3">
                {/* Thumbnail */}
                {post.images[0] && (
                  <Link to={`/posts/${post.id}`} className="flex-shrink-0">
                    <img
                      src={post.images[0].imageUrl}
                      alt=""
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  </Link>
                )}

                <div className="min-w-0 flex-1">
                  <Link
                    to={`/posts/${post.id}`}
                    className="text-sm font-medium text-gray-900 hover:underline"
                  >
                    {post.caption
                      ? post.caption.length > 80
                        ? post.caption.slice(0, 80) + "..."
                        : post.caption
                      : "No caption"}
                  </Link>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {post.images.length} image{post.images.length > 1 ? "s" : ""}
                    {" · "}
                    {post.likeCount} like{post.likeCount !== 1 ? "s" : ""}
                    {" · "}
                    {post.commentCount} comment{post.commentCount !== 1 ? "s" : ""}
                  </p>
                </div>

                <button
                  onClick={() => handleDelete(post)}
                  className="rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
